<script lang="ts" setup>
import { marked } from 'marked';
import { useSnippet } from '~/composables/useSnippet';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreAutochangeData } from '~/stores/strapi/autochangeData';
import { useStoreSystemHosts } from '~/stores/strapi/systemHosts';
import extendedTables from '~/utils/extendedTables';
import type { IAutochangeData } from '~/types/autochangeData';

const props = defineProps({
  text: {
    type: String,
    default: '',
  },
  autoLinkParse: {
    type: Boolean,
    default: false,
  },
  isLinkNewPageOpen: {
    type: Boolean,
    default: false,
  },
  postrender: {
    type: Function,
    default: (str: string): string => str,
  },
});

const storeSsytemHosts = useStoreSystemHosts();
const storeRoot = useStoreRoot();
const storeAutochangeData = useStoreAutochangeData();
const storePlayer = useStoreSsPlayer();
const snippet = useSnippet();

const contentBlockInternal = ref<string | null>(null);
const contentBlock = ref<string | null>(null);
const contentBlockRefreshKey = ref<number>(0);

watch([contentBlockInternal, () => storePlayer.playerCurrency], () => {
  contentBlockRefreshKey.value += 1;
  contentBlock.value = contentBlockInternal.value
    ? snippet.format(contentBlockInternal.value)
    : contentBlockInternal.value;
});

watch(
  () => props.text,
  () => initMarkdown()
);

const replaceAutochangedTarget = (match: string, autochangeItems: IAutochangeData[]) => {
  const oddUnderscoresAmount = 1;

  const croppedKey = match.slice(oddUnderscoresAmount, match.length - oddUnderscoresAmount);

  const replaycment = autochangeItems.find((changeItem) => changeItem.label === croppedKey);

  return replaycment?.value ?? match;
};

const getMd = (text: string) => {
  if (text) {
    return (
      String(text)
        // href links parser, because content manager is not so smart
        .replace(/\[([^[]+)\]\(([^(]+)\)/gm, (...v: string[]) => {
          if (v[2] && v[2].startsWith('http')) {
            return `[${v[1]}](${v[2]})`;
          } else {
            let domain = `${
              storeRoot.userLang === storeRoot.defaultLang ? '' : '/' + storeRoot.userLang
            }`;
            if (v[2] === '/') {
              return `[${v[1]}](${domain || '/'})`;
            }
            if (v[2] && v[2][0] !== '/') {
              domain += '/';
            }
            return `[${v[1]}](${domain}${v[2]})`;
          }
        })
        .replace(/%domain_regional%/gm, () => storeSsytemHosts.regional)
        .replace(/%[a-z0-9\-_]+%/gm, (match: string) =>
          replaceAutochangedTarget(match, storeAutochangeData.autochangeItems)
        )
    );
  }
  return '';
};

const initMarkdown = () => {
  const renderer = new marked.Renderer();
  // @ts-ignore
  renderer.oldImage = renderer.image;
  renderer.image = function (href, title, text) {
    const videos = ['webm', 'mp4', 'mov'];
    const splitedHref = href?.split('.') || [];
    const filetype = splitedHref[splitedHref.length - 1];
    if (videos.includes(filetype)) {
      const out =
        '<video autoplay playsinline muted loop alt="' +
        text +
        '">' +
        '  <source src="' +
        href +
        '" type="video/' +
        filetype +
        '">' +
        '</video>';
      return out;
    } else {
      // @ts-ignore
      return renderer.oldImage(href, title, text);
    }
  };
  marked.use(extendedTables());
  let parsed = marked.parse(getMd(props.text), { gfm: false, renderer, async: false }) as string;
  parsed = parsed.split('<a href="http').join('<a target="_blank" href="http');

  if (props.isLinkNewPageOpen) {
    parsed = parsed.split('<a').join('<a target="_blank"');
  }

  contentBlockInternal.value = (props.postrender as any)(parsed);
  contentBlock.value = contentBlockInternal.value
    ? snippet.format(contentBlockInternal.value)
    : contentBlockInternal.value;
};

initMarkdown();
</script>

<template>
  <div :key="contentBlockRefreshKey" v-html="contentBlock" />
</template>
